import { Form, Input } from 'antd';
import { Button, Col, Colors, IcAddress, IcInStore, IcMenuChange, IcPhone, Row, Text } from 'basic-ui-lib';
import { FooterMobile } from 'layouts/widgets/BottomMobile';
import HeaderMobile from 'layouts/widgets/HeaderMobile';
import { IDevice, IScreenDetail } from 'modules/dashboard/interfaces/api';
import dashboardApis from 'modules/dashboard/services/apis';
import { IStore } from 'modules/store/interfaces/api';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { getMessageError, IResponseData } from 'services/response';
import styled from 'styled-components';
import { setPageLoading } from 'widgets/PageLoading';
import DeviceInfoMobileModal, { DeviceInfoMobileModalRef } from './DeviceInfoMobileModal';
import toast from 'utils/toast';
import ContentPickerSectionMobile, { ContentFormValues } from 'basic-ui-lib/components/ContentPickerSectionMobile';
import { LayoutCode } from 'modules/channel/services/constants';
import EditorHeaderMobile from './EditorHeaderMobile';
import { ScreenStatus } from 'modules/dashboard/services/constants';
import navigate from 'utils/navigate';

interface NewScreenMobileProps {
  screenId?: string;
}

const NewScreenMobile = ({ screenId }: NewScreenMobileProps) => {
  const location = useLocation();
  const { store } = (location.state as { store: IStore }) || {};
  const [code, setCode] = useState('');
  const deviceInfoMobileModalRef = useRef<DeviceInfoMobileModalRef>(null);
  const [step, setStep] = useState<number>(1);
  const [form] = Form.useForm<ContentFormValues>();
  const [device, setDevice] = useState<IDevice | null>(null);

  useEffect(() => {
    if (screenId) {
      const fetchDetail = async () => {
        try {
          setPageLoading(true);
          const res: IResponseData<IScreenDetail> = await dashboardApis.getScreenDetail(screenId);

          if (res?.data?.data) {
            const detail = res.data.data;
            setDevice(detail.deviceInfo || null);
            setStep(2);
            form.setFieldsValue({
              deviceId: detail.deviceInfo?.deviceId || '',
              storeId: detail.storeId || '',
              screenName: detail.screenName,
              playListId: detail.playListId,
              layoutCode: detail.layoutCode as LayoutCode,
              contentItems: detail.contentItems,
            });
          }
        } catch (error) {
          toast.error(getMessageError(error) || 'Failed to fetch screen detail');
        } finally {
          setPageLoading(false);
        }
      };

      fetchDetail();
    }
  }, [screenId]);

  const handleContinue = async () => {
    try {
      setPageLoading(true);
      const res: IResponseData<IDevice> = await dashboardApis.checkDeviceByCode(code.trim());

      if (res?.data?.data) {
        deviceInfoMobileModalRef.current?.open(res?.data?.data);
        setDevice(res?.data?.data);
        return;
      } else {
        toast.error('Invalid screen code. Please try again.');
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to verify pairing code.');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSuccess = () => {
    setStep(2);
  };

  const buildCreatePayload = (status: ScreenStatus) => {
    const values = form.getFieldsValue();
    return {
      deviceId: values.deviceId || '',
      storeId: values.storeId || '',
      screenName: values.screenName || '',
      playListId: values.playListId || '',
      status: status,
      contentItems: values.contentItems || [],
      layoutCode: values.layoutCode || LayoutCode.MAIN_ONLY,
      duration: values.contentItems ? values.contentItems.reduce((sum, item) => sum + (item.duration || 0), 0) : 0,
    };
  };

  const handleSaveDraft = async () => {
    try {
      if (!screenId) {
        const payload = buildCreatePayload(ScreenStatus.DRAFT);
        const res = await dashboardApis.createScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen saved as draft');
          navigate('/screen');
        }
      } else if (screenId) {
        const payload = buildUpdatePayload(ScreenStatus.DRAFT);
        const res = await dashboardApis.updateScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen updated as draft');
          navigate('/screen');
        }
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to save draft'));
    }
  };

  const buildUpdatePayload = (status: ScreenStatus) => {
    const values = form.getFieldsValue();
    return {
      id: screenId || '',
      deviceId: values.deviceId,
      storeId: values.storeId,
      screenName: values.screenName,
      playListId: values.playListId,
      layoutCode: values.layoutCode,
      status: status,
      note: 'Updated from dashboard',
      duration: values.contentItems ? values.contentItems.reduce((sum, item) => sum + (item.duration || 0), 0) : 0,
      contentItems: values.contentItems,
    };
  };

  const handlePublish = async () => {
    try {
      if (!screenId) {
        const payload = buildCreatePayload(ScreenStatus.ONLINE);
        const res = await dashboardApis.createScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen published successfully');
          navigate('/screen');
        }
      } else if (screenId) {
        const payload = buildUpdatePayload(ScreenStatus.ONLINE);
        const res = await dashboardApis.updateScreen(payload);
        if (res?.data?.data) {
          toast.success('Screen published successfully');
          navigate('/screen');
        }
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to publish screen'));
    }
  };

  return (
    <Container>
      <HeaderMobile title='New Screen' />
      <Content>
        {step === 1 ? (
          <>
            <StoreInfo>
              <Row flex={1} width='100%' display='flex' justifyContent='space-between' alignItems='center'>
                <Row>
                  <Row
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    padding={12}
                    borderRadius={100}
                    backgroundColor={Colors.neutral.bg[2]}
                  >
                    <IcInStore width={32} height={32} />
                  </Row>
                  <Text title_h6 semibold>
                    {store?.storeName || 'Store Name'}
                  </Text>
                </Row>

                <button>
                  <Row
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                    padding={8}
                    borderRadius={10}
                    backgroundColor={Colors.primary[1]}
                  >
                    <IcMenuChange width={24} height={24} fill={Colors.primary[6]} />
                  </Row>
                </button>
              </Row>
              <Col gap={4} flex={1} width='100%'>
                <Row>
                  <IcPhone width={14} height={14} />
                  <Text caption_3 medium color='neutral.text.5'>
                    {store?.phone || 'Store Phone'}
                  </Text>
                </Row>
                <Row>
                  <IcAddress width={14} height={14} />
                  <Text caption_3 medium color='neutral.text.5'>
                    {store?.address || 'Store Address'}
                  </Text>
                </Row>
              </Col>
            </StoreInfo>
            <CodeArea>
              <Text body_1 semibold color='neutral.text.3'>
                SCREEN CODE
              </Text>
              <Input
                placeholder='Enter screen code'
                className='input-screen-code'
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </CodeArea>
          </>
        ) : (
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              layoutCode: LayoutCode.MAIN_ONLY,
              storeId: store?.storeId,
              screenName: device?.deviceName || '',
              deviceId: device?.id || '',
              contentItems: [],
            }}
          >
            <Form.Item name='deviceId' noStyle>
              <Input type='hidden' />
            </Form.Item>
            <Form.Item name='screenName' noStyle>
              <Input type='hidden' />
            </Form.Item>
            <Form.Item name='contentItems' noStyle>
              <Input type='hidden' />
            </Form.Item>
            <EditorHeaderMobile form={form} />
            <ContentPickerSectionMobile type='SCREEN' />
          </Form>
        )}
      </Content>
      <FooterMobile>
        {step === 1 ? (
          <>
            <Button type_cancel lg onClick={() => navigate('/screen')}>
              Cancel
            </Button>
            <Button type_second lg onClick={handleContinue}>
              Ok
            </Button>
          </>
        ) : (
          <>
            <Button type_cancel lg onClick={() => navigate('/screen')}>
              Cancel
            </Button>
            <Button type_new lg onClick={handleSaveDraft}>
              Draft
            </Button>
            <Button type_second lg onClick={handlePublish}>
              Publish
            </Button>
          </>
        )}
      </FooterMobile>
      <DeviceInfoMobileModal ref={deviceInfoMobileModalRef} onSuccess={handleSuccess} />
    </Container>
  );
};

export default NewScreenMobile;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const Content = styled.div`
  flex: 1;
`;

const StoreInfo = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 8px;
  background-color: ${Colors.neutral.bg[1]};
`;

const CodeArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px 16px;

  .input-screen-code {
    width: 100%;
    height: 58px;
    padding: 0px 16px;
    border: 1px solid ${Colors.neutral.border[1]};
    border-radius: 20px;
    font-size: 16px;
    text-align: center;
    font-weight: 500;
  }
`;
